/**
 * One-shot reveal of a webhook's HMAC secret.
 *
 * Unlike API keys (which can be revealed again from the list), webhook
 * secrets are hashed-stored after the create response - there is no
 * "show again" path. The dialog warns the operator explicitly and the
 * close button is gated by an acknowledgement.
 */

import { useState } from 'react'
import { AlertTriangle, Check, Copy } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { useFormatters } from '@/shared/lib/formatting'

export interface RevealWebhookSecretDialogProps {
  open: boolean
  /** The plaintext HMAC secret returned by POST /webhooks/ exactly once. */
  secret: string | null
  /** Webhook description, shown for confirmation. */
  description?: string
  /** ISO timestamp until which the previous secret stays valid (rotate only). */
  previousSecretExpiresAt?: string | null
  onClose: () => void
}

export function RevealWebhookSecretDialog({
  open,
  secret,
  description,
  previousSecretExpiresAt,
  onClose,
}: RevealWebhookSecretDialogProps) {
  const { formatDateTime } = useFormatters()
  const [copied, setCopied] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)

  function handleCopy() {
    if (!secret) return
    navigator.clipboard.writeText(secret)
    setCopied(true)
    toast.success('Secret copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  function handleClose() {
    setCopied(false)
    setAcknowledged(false)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // Block dismiss via backdrop/ESC unless acknowledged - the
        // secret is genuinely one-shot.
        if (!v && acknowledged) handleClose()
      }}
    >
      <DialogContent className="max-w-lg" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Webhook signing secret</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {description && (
            <p className="text-[13px] font-medium text-[var(--ink)]">{description}</p>
          )}

          <div className="flex items-start gap-2 rounded-lg border-[0.5px] border-[var(--gold-border)] bg-[var(--gold-bg)] p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-ink)]" />
            <p className="text-[12px] leading-relaxed text-[var(--gold-ink)]">
              This is the only time you'll see this secret. Store it in your
              vault now. You can rotate to a fresh one later if you lose it,
              but you won't be able to recover this value.
            </p>
          </div>

          {secret ? (
            <div className="rounded-lg bg-[var(--bg-subtle)] p-4">
              <code className="block break-all font-mono text-[12px] text-[var(--ink)]">
                {secret}
              </code>
            </div>
          ) : null}

          {previousSecretExpiresAt ? (
            <p className="text-[12px] leading-relaxed text-[var(--ink-3)]">
              Your previous secret stays valid until{' '}
              <span className="font-medium text-[var(--ink-2)]">
                {formatDateTime(previousSecretExpiresAt)}
              </span>
              . Deliveries sent during this window include both signatures
              (<code className="font-mono text-[11px]">X-Pulse-Signature</code>{' '}
              and <code className="font-mono text-[11px]">X-Pulse-Signature-Previous</code>)
              so receivers can switch over without dropping events.
            </p>
          ) : null}

          <label className="flex cursor-pointer items-center gap-2.5">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
            />
            <span className="text-[12px] text-[var(--ink-2)]">
              I've copied the secret somewhere safe.
            </span>
          </label>
        </div>

        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button onClick={handleCopy} variant="outline" disabled={!secret}>
            {copied ? (
              <Check className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <Copy className="mr-1.5 h-3.5 w-3.5" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button onClick={handleClose} disabled={!acknowledged}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
